import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { api, errMsg, eur, fileUrl, uid, useSeo } from "../lib/api";
import { useCart, useSite } from "../lib/store";
import { Page, PageTitle, Empty, Loading } from "../components/site/Bits";

const Field = ({ label, ...p }) => <label className="text-sm font-semibold flex flex-col gap-1">{label}<input {...p} className="h-11 border border-[#E6DFD5] bg-white px-3 font-normal" /></label>;

import { useSpamProtection } from "../components/site/SpamProtection";

export function Cart() {
  const spam = useSpamProtection();
  const { cart, setQty, remove } = useCart();
  const site = useSite();
  const [delivery, setDelivery] = useState("shipping");
  const [q, setQ] = useState(null);
  const [c, setC] = useState({ name: "", email: "", phone: "", street: "", zip: "", city: "", country: "AT" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const token = useRef(uid());
  useSeo("Warenkorb");
  const items = cart.map(({ kind, artwork_id, variant_id, find_id, qty }) => ({ kind, artwork_id, variant_id, find_id, qty }));
  useEffect(() => {
    if (!cart.length) return setQ(null);
    api.post("/shop/quote", { items, delivery }).then((r) => setQ(r.data)).catch((e) => setErr(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items), delivery]);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const pay = async (e) => {
    e.preventDefault();
    if (busy || !spam.ready) return;
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/shop/checkout", { items, delivery, name: c.name, email: c.email, phone: c.phone, address: delivery === "shipping" ? { street: c.street, zip: c.zip, city: c.city, country: c.country } : null,
        turnstile_token: spam.token, origin_url: window.location.origin, qr_code: sessionStorage.getItem("kf_qr"), source: sessionStorage.getItem("kf_src"), client_token: token.current });
      window.location.href = r.data.checkout_url;
    } catch (e2) {
      setErr(errMsg(e2));
      spam.reset();
      token.current = uid();
      setBusy(false);
    }
  };
  if (!cart.length) return <Page><PageTitle title="Warenkorb" /><Empty testid="cart-empty">Dein Warenkorb ist leer. <Link to="/prints" className="underline">Prints ansehen</Link></Empty></Page>;
  const avail = site?.checkout?.available;
  return (
    <Page>
      <PageTitle title="Warenkorb" />
      {site?.checkout?.mode === "test" && <p className="mb-6 bg-[#FFC72C] px-4 py-2 font-mono text-sm" data-testid="checkout-testmode">TESTMODUS – Zahlung mit Stripe-Testkarte 4242 4242 4242 4242, es wird nichts belastet.</p>}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
        <div className="space-y-4">
          {cart.map((x) => {
            const line = q?.lines.find((l) => (l.variant_id || l.find_id || l.artwork_id) === (x.variant_id || x.find_id || x.artwork_id) && l.kind === x.kind);
            return (
              <div key={x.key} className="grid grid-cols-[80px_1fr_auto] gap-4 items-center bg-white border border-[#E6DFD5] p-3" data-testid={`cart-item-${x.kind}`}>
                <div className="passe h-20 w-20 relative">{x.image && <img src={fileUrl(x.image)} alt="" className="absolute inset-0 w-full h-full object-contain p-1" />}</div>
                <div><p className="font-bold">{x.title}</p><p className="text-sm text-[#6E6B65]">{x.label}</p>{line && <p className="text-xs text-[#6E6B65]">Verkäufer: {line.seller}{line.provider ? ` · Anbieter: ${line.provider}` : ""}</p>}
                  {x.kind === "print" && <input type="number" min={1} max={20} value={x.qty} onChange={(e) => setQty(x.key, Number(e.target.value))} aria-label="Menge" className="mt-2 h-10 w-20 border border-[#E6DFD5] px-2" data-testid="cart-qty" />}</div>
                <div className="text-right"><p className="font-bold">{line ? eur(line.total) : "–"}</p><button onClick={() => remove(x.key)} aria-label="Entfernen" className="mt-2 h-10 w-10 inline-grid place-items-center hover:text-[#FF5A5F]" data-testid="cart-remove"><Trash2 size={18} /></button></div>
              </div>);
          })}
          {q?.errors?.length > 0 && <div className="bg-[#FFEAEA] p-3 text-sm" role="alert" data-testid="cart-errors">{q.errors.map((e) => <p key={e}>{e}</p>)}</div>}
        </div>
        <form onSubmit={pay} className="bg-[#F4EFEA] p-6 space-y-4 h-fit" data-testid="checkout-form">
          <fieldset className="flex gap-3"><legend className="text-sm font-semibold mb-2">Lieferung</legend>
            {[["shipping", "Versand"], ["pickup", "Abholung"]].map(([v, l]) => <label key={v} className={`flex-1 h-12 border-2 flex items-center justify-center gap-2 font-semibold cursor-pointer ${delivery === v ? "border-[#121212] bg-white" : "border-[#E6DFD5]"}`}><input type="radio" className="sr-only" checked={delivery === v} onChange={() => setDelivery(v)} data-testid={`delivery-${v}`} />{l}</label>)}
          </fieldset>
          {delivery === "pickup" && q?.pickup_location && <p className="text-sm">Abholort: {q.pickup_location}</p>}
          <div className="grid gap-3"><Field label="Name *" required value={c.name} onChange={set("name")} autoComplete="name" data-testid="checkout-name" />
            <Field label="E-Mail *" type="email" required value={c.email} onChange={set("email")} autoComplete="email" data-testid="checkout-email" />
            <Field label="Telefon" value={c.phone} onChange={set("phone")} autoComplete="tel" data-testid="checkout-phone" />
            {delivery === "shipping" && <><Field label="Straße & Nr. *" required value={c.street} onChange={set("street")} autoComplete="street-address" data-testid="checkout-street" />
              <div className="grid grid-cols-[110px_1fr] gap-3"><Field label="PLZ *" required value={c.zip} onChange={set("zip")} autoComplete="postal-code" data-testid="checkout-zip" /><Field label="Ort *" required value={c.city} onChange={set("city")} autoComplete="address-level2" data-testid="checkout-city" /></div></>}
          </div>
          {q && <dl className="text-sm space-y-1 border-t border-[#E6DFD5] pt-4" data-testid="checkout-summary">
            <div className="flex justify-between"><dt>Zwischensumme</dt><dd>{eur(q.subtotal)}</dd></div>
            <div className="flex justify-between"><dt>Versand</dt><dd data-testid="checkout-shipping">{eur(q.shipping)}</dd></div>
            <div className="flex justify-between font-bold text-lg"><dt>Gesamt</dt><dd data-testid="checkout-total">{eur(q.total)}</dd></div>
            <p className="text-xs text-[#6E6B65]">Verkäufer: {q.seller}{!q.seller_confirmed && " (unbestätigt)"} {q.tax_note && `· ${q.tax_note}`}</p></dl>}
          {spam.widget}
          {err && <p className="text-sm text-[#c2181d] font-semibold" role="alert" data-testid="checkout-error">{err}</p>}
          {!avail && <p className="text-sm bg-white p-3" data-testid="checkout-unavailable">Die Kaufabwicklung ist noch nicht freigeschaltet. Bitte nutze die Anfrage beim jeweiligen Werk.</p>}
          <button disabled={busy || !spam.ready || !avail || !q || q.errors.length > 0} className="btn-ink h-12 w-full font-semibold disabled:opacity-50" data-testid="checkout-submit">{busy ? "Weiter zur Zahlung …" : "Zahlungspflichtig bestellen"}</button>
          <p className="text-xs text-[#6E6B65]">Mit der Bestellung gelten <Link className="underline" to="/rechtliches/agb">Geschäftsbedingungen</Link>, <Link className="underline" to="/rechtliches/rueckgabe">Rückgabe</Link> und <Link className="underline" to="/rechtliches/datenschutz">Datenschutz</Link>.</p>
        </form>
      </div>
    </Page>
  );
}

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const { clear } = useCart();
  const [st, setSt] = useState(null);
  useEffect(() => {
    let n = 0, stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/shop/status/${sp.get("session_id")}`);
        setSt(r.data);
        if (r.data.payment_status === "paid") return clear();
      } catch (e) {
        setSt({ error: true });
      }
      if (!stop && ++n < 10) setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const paid = st?.payment_status === "paid";
  return (
    <Page className="max-w-2xl">
      <PageTitle title={paid ? "Danke für deine Bestellung!" : "Zahlung wird geprüft …"} />
      <p data-testid="payment-status">{paid ? "Die Zahlung ist bestätigt. Eine Bestellbestätigung wird an deine E-Mail gesendet, sobald der Mailversand eingerichtet ist." : st?.error ? "Status konnte nicht geladen werden." : "Bitte einen Moment Geduld – wir warten auf die Bestätigung des Zahlungsanbieters."}</p>
      <Link to={`/bestellung/${sp.get("order")}?t=${sp.get("t")}`} className="btn-ink inline-flex h-12 px-6 items-center mt-8 font-semibold" data-testid="order-link">Bestellung ansehen</Link>
    </Page>
  );
}

export function PaymentCancel() {
  const [sp] = useSearchParams();
  const [done, setDone] = useState(false);
  useEffect(() => {
    api.post(`/shop/orders/${sp.get("order")}/cancel?t=${sp.get("t")}`).finally(() => setDone(true));
  }, [sp]);
  return <Page className="max-w-2xl"><PageTitle title="Zahlung abgebrochen" /><p data-testid="payment-cancelled">{done ? "Es wurde nichts belastet. Reservierte Artikel sind wieder freigegeben; dein Warenkorb bleibt erhalten." : "Wird verarbeitet …"}</p><Link to="/warenkorb" className="btn-ink inline-flex h-12 px-6 items-center mt-8 font-semibold">Zurück zum Warenkorb</Link></Page>;
}

const OST = { payment_open: "Zahlung offen", paid: "Bezahlt", in_production: "In Produktion", ready_pickup: "Bereit zur Abholung", shipped: "Versendet", completed: "Abgeschlossen", cancelled: "Storniert" };
export { OST };

export function OrderView() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/shop/orders/${id}?t=${sp.get("t") || ""}`).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id, sp]);
  useSeo("Bestellung");
  if (err) return <Page><Empty testid="order-error">{err}</Empty></Page>;
  if (!o) return <Loading />;
  return (
    <Page className="max-w-3xl">
      <PageTitle eyebrow={o.mode === "test" ? "Testbestellung" : "Bestellung"} title={o.number} />
      <p className="text-lg font-semibold" data-testid="order-status">Status: {OST[o.status]}{o.refund_status !== "none" && ` · Erstattet: ${eur(o.refunded_amount)}`}</p>
      {o.tracking && <p className="text-sm mt-1">Sendungsverfolgung: {o.tracking}</p>}
      <div className="mt-6 bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5]">{o.items.map((i) => <div key={i.id} className="p-4 flex justify-between gap-4 text-sm"><span>{i.qty} × {i.description}<br /><span className="text-[#6E6B65]">Verkäufer: {i.seller} · {i.conditions}</span></span><strong>{eur(i.total)}</strong></div>)}
        <div className="p-4 flex justify-between text-sm"><span>Versand ({o.delivery === "pickup" ? "Abholung" : "Versand"})</span><span>{eur(o.shipping)}</span></div>
        <div className="p-4 flex justify-between font-bold"><span>Gesamt</span><span>{eur(o.total)}</span></div></div>
    </Page>
  );
}

